// IMINT-annotator: tegn bounding-bokser med merkelapper på et bilde. Boksene
// lagres i normaliserte koordinater (0–1) så de skalerer uansett visningsstørrelse
// og kan deles i intel-produktet. Ren DOM-overlegg over <img> (ingen canvas-
// piksleslesing → ingen CORS-krav). Merkelapper settes via textContent (ingen XSS).
import type { ImintBox } from '@skade/contracts';
import { sporTekst } from './shell.js';

export class ImintAnnotator {
  readonly el: HTMLElement;
  private img: HTMLImageElement;
  private overlay: HTMLElement;
  private boxes: ImintBox[];

  constructor(imageUrl: string, boxes: ImintBox[] = [], private readOnly = false) {
    this.boxes = boxes.map((b) => ({ ...b }));
    this.el = div('imint-annot');
    const stage = div('imint-stage');
    this.img = document.createElement('img');
    this.img.className = 'imint-img';
    this.img.alt = 'IMINT';
    // Kun https:/data:image — hindrer at et delt produkt får hver mottakers
    // nettleser til å beacone ut til en angriper-URL når det åpnes.
    this.img.src = safeImageUrl(imageUrl);
    this.overlay = div('imint-overlay');
    stage.append(this.img, this.overlay);
    this.el.append(stage);
    if (!this.readOnly) this.attachDrawing();
    this.img.onload = () => this.render();
    this.render();
  }

  getBoxes(): ImintBox[] {
    return this.boxes.map((b) => ({ ...b }));
  }

  private attachDrawing() {
    let start: { x: number; y: number } | null = null;
    let ghost: HTMLElement | null = null;
    const rel = (e: MouseEvent) => {
      const r = this.overlay.getBoundingClientRect();
      return { x: clamp01((e.clientX - r.left) / r.width), y: clamp01((e.clientY - r.top) / r.height) };
    };
    this.overlay.onmousedown = (e) => {
      start = rel(e);
      ghost = div('imint-box ghost');
      this.overlay.append(ghost);
    };
    this.overlay.onmousemove = (e) => {
      if (!start || !ghost) return;
      const c = rel(e);
      place(ghost, Math.min(start.x, c.x), Math.min(start.y, c.y), Math.abs(c.x - start.x), Math.abs(c.y - start.y));
    };
    this.overlay.onmouseup = (e) => {
      if (!start) return;
      const c = rel(e);
      const x = Math.min(start.x, c.x), y = Math.min(start.y, c.y), w = Math.abs(c.x - start.x), h = Math.abs(c.y - start.y);
      ghost?.remove(); ghost = null; start = null;
      if (w < 0.01 || h < 0.01) return; // for lite — ignorer
      sporTekst({
        tittel: 'Merkelapp for boksen',
        tekst: 'Hva ser du?',
        onSvar: (svar) => {
          const label = (svar || '').trim();
          this.boxes.push({ x, y, w, h, label });
          this.render();
        },
      });
    };
  }

  private render() {
    this.overlay.innerHTML = '';
    this.boxes.forEach((b, i) => {
      const box = div('imint-box');
      place(box, b.x, b.y, b.w, b.h);
      const lab = div('imint-box-label');
      lab.textContent = b.label || `#${i + 1}`;
      box.append(lab);
      if (!this.readOnly) {
        box.title = 'Klikk for å slette';
        box.onmousedown = (e) => e.stopPropagation(); // ikke start ny boks
        box.onclick = (e) => { e.stopPropagation(); this.boxes.splice(i, 1); this.render(); };
      }
      this.overlay.append(box);
    });
  }
}

/** Tillat kun trygge bilde-skjemaer (https / data:image). */
/**
 * Tillatte bilde-URL-er. Alt annet blir tom streng (bildet vises ikke).
 *
 * Formålet er å hindre at et DELT intel-produkt får hver mottakers nettleser til
 * å hente et bilde fra en angriper-URL — altså en beacon som avslører hvem som
 * åpnet produktet og når.
 *
 * ⚠ SAMME-OPPHAV-STIER MÅ VÆRE MED, og de manglet.
 * Første utgave tillot bare `https://` og `data:image/`. Hubben serverer sitt
 * eget medialager på RELATIV sti (`/media/<navn>`), så et feltbilde fra GNÅ ga
 * tom streng og annotatoren viste blankt — funnet før første kobling, 2026-08-17.
 *
 * `^/(?!/)` er nøyaktig det som er trygt: én ledende skråstrek er samme opphav og
 * kan ikke beacone noe sted. To skråstreker («//angriper.no/x.jpg») er
 * PROTOKOLL-RELATIV og altså kryssopphav — den skal fortsatt avvises, og det er
 * derfor negativt lookahead står der i stedet for en enklere `^/`-test.
 */
function safeImageUrl(url: string): string {
  const u = (url || '').trim();
  const trygg =
    /^https:\/\//i.test(u) ||
    /^data:image\//i.test(u) ||
    /^\/(?!\/)/.test(u); // samme opphav (f.eks. /media/…), aldri //vert
  return trygg ? u : '';
}

/** Eksponert for test: guarden er sikkerhetsnær og skal ha egne prøver. */
export const _testSafeImageUrl = safeImageUrl;
function clamp01(n: number): number { return Math.max(0, Math.min(1, n)); }
function place(el: HTMLElement, x: number, y: number, w: number, h: number) {
  el.style.left = `${x * 100}%`;
  el.style.top = `${y * 100}%`;
  el.style.width = `${w * 100}%`;
  el.style.height = `${h * 100}%`;
}
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
